import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import GroupsIcon from '@mui/icons-material/Groups';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import { Team, TeamMember } from '../types';
import styles from './TeamCards.module.css';

type TeamCardsProps = {
  teams: Team[];
  currentUserId?: string;
  isLeader?: boolean;
  title?: string;
  description?: string;
  hideEmptyTeams?: boolean;
  onSelectMember?: (member: TeamMember, team: Team) => void;
};

const AVATAR_PALETTE = [
  { background: '#d8f3e3', color: '#2f7a54' },
  { background: '#f7d7e4', color: '#9a4b66' },
  { background: '#e4d9fa', color: '#5b4db8' },
  { background: '#f7e7c6', color: '#8a6a2b' },
  { background: '#d6e7f8', color: '#3d6d99' },
];

const getInitials = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const colorFromKey = (
  key: string,
  palette: { background: string; color: string }[]
) => {
  const hash = Array.from(key).reduce(
    (sum, character) => sum + character.charCodeAt(0),
    0
  );
  return palette[hash % palette.length];
};

const memberCountLabel = (count: number) =>
  count === 1 ? '1 deltaker' : `${count} deltakere`;

const isCurrentUserOnTeam = (team: Team, currentUserId?: string) =>
  Boolean(
    currentUserId && team.members.some((member) => member.id === currentUserId)
  );

const isHiddenFromMembers = (member: TeamMember) => Boolean(member.isHidden);

const isHiddenFromViewer = (
  member: TeamMember,
  currentUserId?: string,
  isLeader?: boolean
) => isHiddenFromMembers(member) && !isLeader && member.id !== currentUserId;

const teamCountLabel = (
  team: Team,
  currentUserId?: string,
  isLeader?: boolean
) => {
  const hiddenCount = team.members.filter((member) =>
    isLeader
      ? isHiddenFromMembers(member)
      : isHiddenFromViewer(member, currentUserId, isLeader)
  ).length;
  const count = memberCountLabel(team.members.length);
  if (hiddenCount === 0) {
    return count;
  }
  return isLeader
    ? `${count} · ${hiddenCount} anonymisert`
    : `${count} · ${hiddenCount} skjult`;
};

const MemberAvatar: React.FC<{ member: TeamMember }> = ({ member }) => {
  const palette = colorFromKey(member.id || member.name, AVATAR_PALETTE);

  if (member.avatarUrl) {
    return <img src={member.avatarUrl} alt="" className={styles.avatar} />;
  }

  return (
    <span
      className={styles.initials}
      style={{ background: palette.background, color: palette.color }}
    >
      {getInitials(member.name)}
    </span>
  );
};

const HiddenMemberRow: React.FC = () => (
  <div className={`${styles.memberRow} ${styles.memberRowHidden}`}>
    <span className={styles.hiddenIcon} aria-hidden="true">
      <LockOutlinedIcon />
    </span>
    <div className={styles.memberText}>
      <span className={styles.hiddenName}>Deltaker skjult</span>
      <span className={styles.hiddenHint}>Har ikke samtykket til deling</span>
    </div>
  </div>
);

export const TeamCards: React.FC<TeamCardsProps> = ({
  teams,
  currentUserId,
  isLeader = false,
  title,
  description,
  hideEmptyTeams = true,
  onSelectMember,
}) => {
  const orderedTeams = useMemo(() => {
    const visible = hideEmptyTeams
      ? teams.filter((team) => team.members.length > 0)
      : teams;
    const yours = visible.filter((team) =>
      isCurrentUserOnTeam(team, currentUserId)
    );
    const others = visible.filter(
      (team) => !isCurrentUserOnTeam(team, currentUserId)
    );
    return [...yours, ...others];
  }, [teams, currentUserId, hideEmptyTeams]);

  const hasHiddenMembers = orderedTeams.some((team) =>
    team.members.some((member) =>
      isLeader
        ? isHiddenFromMembers(member)
        : isHiddenFromViewer(member, currentUserId, isLeader)
    )
  );

  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>(
    () => {
      const yours = teams.find((team) =>
        isCurrentUserOnTeam(team, currentUserId)
      );
      return yours ? { [yours.id]: true } : {};
    }
  );

  useEffect(() => {
    const yourTeam = orderedTeams.find((team) =>
      isCurrentUserOnTeam(team, currentUserId)
    );
    if (!yourTeam) {
      return;
    }
    setExpandedIds((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, yourTeam.id)) {
        return prev;
      }
      return { [yourTeam.id]: true };
    });
  }, [orderedTeams, currentUserId]);

  const toggleTeam = (teamId: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [teamId]: !prev[teamId],
    }));
  };

  if (orderedTeams.length === 0) {
    return <p className={styles.empty}>No teams to display.</p>;
  }

  return (
    <div className={styles.wrap}>
      {(title || description) && (
        <div className={styles.heading}>
          {title && (
            <div className={styles.headingRow}>
              <span className={styles.headingIcon} aria-hidden="true">
                <GroupsIcon fontSize="small" />
              </span>
              <h3>{title}</h3>
            </div>
          )}
          {description && <p className={styles.description}>{description}</p>}
        </div>
      )}

      {hasHiddenMembers && (
        <div className={styles.consentNotice} role="status">
          <span className={styles.consentNoticeIcon} aria-hidden="true">
            <LockOutlinedIcon />
          </span>
          <p>
            {isLeader
              ? 'Deltakere uten samtykke vises for deg, men er anonymisert for medlemmer.'
              : 'Noen deltakere kan være skjult fordi de ikke har samtykket til deling av kontaktinformasjon.'}
          </p>
        </div>
      )}

      <div className={styles.list}>
        {orderedTeams.map((team) => {
          const isYours = isCurrentUserOnTeam(team, currentUserId);
          const isExpanded = Boolean(expandedIds[team.id]);
          return (
            <article
              key={team.id}
              className={
                isYours
                  ? `${styles.teamCard} ${styles.teamCardYour}`
                  : styles.teamCard
              }
            >
              <button
                type="button"
                className={styles.teamToggle}
                onClick={() => toggleTeam(team.id)}
                aria-expanded={isExpanded}
              >
                <span className={styles.teamMeta}>
                  <span className={styles.teamName}>{team.name}</span>
                  <span className={styles.teamCount}>
                    {teamCountLabel(team, currentUserId, isLeader)}
                  </span>
                </span>
                <span className={styles.chevron}>
                  {isExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
                </span>
              </button>

              {isExpanded && (
                <div className={styles.members}>
                  {team.members.map((member) => {
                    if (isHiddenFromViewer(member, currentUserId, isLeader)) {
                      return <HiddenMemberRow key={member.id} />;
                    }

                    const isYou = member.id === currentUserId;
                    const hiddenForMembers =
                      isLeader && isHiddenFromMembers(member);
                    const rowClassName = isYou
                      ? `${styles.memberRow} ${styles.memberRowYou}`
                      : styles.memberRow;
                    const rowContent = (
                      <>
                        <MemberAvatar member={member} />
                        <div className={styles.memberText}>
                          <span className={styles.memberName}>
                            {member.name}
                          </span>
                          {member.company && (
                            <span className={styles.memberCompany}>
                              {member.company}
                            </span>
                          )}
                          {hiddenForMembers && (
                            <span className={styles.anonymizedBadge}>
                              <VisibilityOffOutlinedIcon />
                              Anonymisert for medlemmer
                            </span>
                          )}
                        </div>
                        {isYou && <span className={styles.youBadge}>Deg</span>}
                      </>
                    );

                    if (onSelectMember) {
                      return (
                        <button
                          key={member.id}
                          type="button"
                          className={`${rowClassName} ${styles.memberRowButton}`}
                          onClick={() => onSelectMember(member, team)}
                          aria-label={
                            hiddenForMembers
                              ? `Vis kundekort for ${member.name}, anonymisert for medlemmer`
                              : `Vis kundekort for ${member.name}`
                          }
                        >
                          {rowContent}
                        </button>
                      );
                    }

                    return (
                      <div key={member.id} className={rowClassName}>
                        {rowContent}
                      </div>
                    );
                  })}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};
